import { useState, useCallback, useEffect } from 'react';
import { _quali } from '@carvana/quali-events/dist';

import Cookie from '../helpers/Cookie';
import type { QualiSurveyProps } from '../types';
import QualiSurvey from './QualiSurvey';

const { useQualiController } = _quali

const isSpam = (name?: string, overrideSpam: boolean = false) => {
  if (!name) {
    return undefined;
  }

  if (overrideSpam) {
    return false;
  }

  return !!Cookie.getCookie('quali-global-asked') || !!Cookie.getCookie(`${name}-asked`);
};

const setAsked = (name?: string, overrideSpam: boolean = false) => {
  if (!name || overrideSpam) {
    return;
  }

  Cookie.setCookie(`${name}-asked`, 'asked', 30);
  Cookie.setCookie('quali-global-asked', 'asked', 30);
};

type ShowModalProps = {
  commentBoxTitle?: string;
  identifier?: string;
  identifierType?: string;
  meta?: { [key: string]: string };
  name: string;
  overrideSpam?: boolean;
  title: string;
  teamName?: string;
};

const QualiSurveyController = (props: Partial<QualiSurveyProps>) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [identifier, setIdentifier] = useState<string | undefined>(undefined);
  const [title, setTitle] = useState<string>('How was your experience today?');
  const [commentBoxTitle, setCommentBoxTitle] = useState<string | undefined>(undefined);
  const [disableSpamProtection, setDisableSpamProtection] = useState<boolean>(false);
  const [name, setName] = useState<string | undefined>(undefined);
  const [identifierType, setIdentifierType] = useState<string | undefined>(undefined);
  const [meta, setMeta] = useState<{ [key: string]: string } | undefined>(undefined);
  const [shouldShowSurveyNext, setShouldShowSurveyNext] = useState<string | undefined>(undefined);
  const [teamName, setTeamName] = useState<string | undefined>(undefined);

  const { onClose } = props

  const onCloseModal = useCallback(() => {
    setAsked(name, disableSpamProtection);
    setIsOpen(false);
    onClose?.()
  }, [disableSpamProtection, name, onClose]);

  const onShowModal = useCallback((values: ShowModalProps) => {
    setCommentBoxTitle(values.commentBoxTitle);
    setDisableSpamProtection(!!values.overrideSpam);
    setIdentifier(values.identifier);
    setIdentifierType(values.identifierType);
    setMeta(values.meta);
    setName(values.name);
    setTitle(values.title);
    setTeamName(values.teamName);

    if (isSpam(values.name, values.overrideSpam)) {
      return;
    }

    setIsOpen(true);
  }, []);

  const onShouldShowModal = useCallback(
    (values: ShowModalProps) => {
      if (shouldShowSurveyNext !== values.name) {
        return;
      }

      setShouldShowSurveyNext(undefined);
      onShowModal({ ...values });
    },
    [onShowModal, shouldShowSurveyNext]
  );

  const onShowSurveyNext = useCallback((values: { name: string }) => {
    setShouldShowSurveyNext(values.name);
  }, []);

  const { sendState } = useQualiController({ onCloseModal, onShouldShowModal, onShowModal, onShowSurveyNext })

  useEffect(() => {
    sendState({ isOpen })
  }, [isOpen, sendState])

  /* eslint-disable react/destructuring-assignment */
  return (
    <QualiSurvey
      commentBoxTitle={props.commentBoxTitle ?? commentBoxTitle}
      identifier={props.identifier ?? identifier}
      identifierType={props.identifierType ?? identifierType}
      isOpen={props.isOpen ?? isOpen}
      meta={props.meta ?? meta}
      name={props.name ?? name}
      onClose={onCloseModal}
      teamName={props.teamName ?? teamName}
      title={props.title ?? title}
    />
  );
}

export default QualiSurveyController
